import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    SelectChangeEvent
} from '@mui/material';
import api from '../../../services/api';

interface User {
    _id?: string;
    name: string;
    email: string;
    role: 'admin' | 'client';
    active: boolean;
    password?: string;
}

interface CustomerModalProps {
    open: boolean;
    onClose: (refreshList: boolean) => void;
    user: User | null;
    onMessage: (message: string, severity: 'success' | 'error') => void;
}

const CustomerModal: React.FC<CustomerModalProps> = ({ open, onClose, user, onMessage }) => {
    const [formData, setFormData] = useState<User>({
        name: '',
        email: '',
        role: 'client',
        active: true,
        password: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                ...user,
                password: '' // Clear password when editing
            });
        } else {
            setFormData({
                name: '',
                email: '',
                role: 'client',
                active: true,
                password: ''
            });
        }
    }, [user]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const endpoint = user ? `/admin/users/${user._id}` : '/admin/users';
            const method = user ? 'put' : 'post';

            const payload = { ...formData };
            if (!payload.password) {
                delete payload.password;
            }

            const response = await api[method](endpoint, payload);
            console.log('User saved successfully:', response.data);
            onMessage(`User ${user ? 'updated' : 'created'} successfully`, 'success');
            onClose(true);
        } catch (error) {
            console.error('Error saving user:', error);
            onMessage(`Error ${user ? 'updating' : 'creating'} user. Please try again.`, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>{user ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <TextField
                            name="name"
                            label="Name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            fullWidth
                        />
                        <TextField
                            name="email"
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            fullWidth
                        />
                        <FormControl fullWidth>
                            <InputLabel id="role-label">Role</InputLabel>
                            <Select
                                labelId="role-label"
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                label="Role"
                                required
                            >
                                <MenuItem value="client">Client</MenuItem>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            name="password"
                            label={user ? "New Password (leave blank to keep current)" : "Password"}
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            required={!user}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose(false)}>Cancel</Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={20} />}
                    >
                        {user ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CustomerModal;
